import { v4 as uuid } from "uuid";
// import firebase from "firebase/app";
import { db } from "../config/firebase";

import { get, push, ref } from "firebase/database";
// Toastr Notification
import { ToastPosition, toast } from "react-toastify";
// Swal Notifications
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  ApplicationFeatures,
  Department,
  Notification,
  PersonalInformation,
  UrlObject,
  UserMetadata,
  selectOption,
  userContext,
} from "../interfaces";

/** Sends a Toastr Notification to the User
 * @param  {string} title Notification title
 * @param  {string} message Notification message
 * @param  {"error" | "success" | "info" | "warning" | "default"} type Notification type
 */
const toastrMessage = (
  message: string = "",
  type: "error" | "success" | "info" | "warning" | "default" = "info",
  autoclose: boolean = true,
) => {
  const position: ToastPosition = "top-right";
  const config = {
    closeButton: true,
    newestOnTop: true,
    closeOnClick: true,
    pauseOnFocusLoss: true,
    autoClose: autoclose ? 5000 : autoclose,
    position: position,
    pauseOnHover: true,
    draggable: true,
    className: "zIndex-inf",
    progress: undefined,
    hideProgressBar: false,
    toastId: uuid(),
  };
  switch (type) {
    case "error":
      toast.error(message, config);
      break;
    case "success":
      toast.success(message, config);
      break;
    case "warning":
      toast.warn(message, config);
      break;
    case "info":
      toast.info(message, config);
      break;
    default:
      toast(message, config);
      break;
  }
};

/** Sends a Swal Notification to the User
 * @param  {string} title Notification title
 * @param  {string} message Notification message
 * @param  {"error" | "success" | "info" | "question"} type Notification type
 */
const swalMessage = (
  title: string,
  message: string,
  type: "error" | "success" | "info" | "question",
) => {
  swalAlert.fire({
    target: ".app-container",
    customClass: {
      denyButton: "btn btn-shadow btn-danger",
      confirmButton: "btn btn-shadow btn-info",
    },
    title: title,
    icon: type,
    html: `<p>${message}</p>`,
  });
};
const swalAlert = withReactContent(Swal);

/** Returns a snapshot of all the users personal information metadata
 * @return {snapshot} all users metadata snapshot
 */
const getAllUsersMetadata = async () => {
  return get(ref(db, "private/usersMetadata"));
};

/** Retrieves and sets users metadata into the state
 * @param {Function} setUsersMetadata users metadata update state function
 */
const getAndSetAllUsersMetadata = (setUsersMetadata: Function) => {
  return get(ref(db, "private/usersMetadata")).then((snapshot) => {
    if (!snapshot.val()) return;
    setUsersMetadata(snapshot.val());
  });
};

/** Retrieves the profile image path based on the User ID
 * @param  {string} id User id
 * @param  {any} firebaseStorage firebase storage object
 * @param  {boolean} compressed returns the compressed image path or not
 * @return {string} image path
 */
const getUserImgUrl = (id: string, firebaseStorage: any, compressed: boolean) => {
  if (firebaseStorage) {
    const st = firebaseStorage;
    try {
      return st.ref(`users/${id}/${id}comp`).getDownloadURL();
    } catch (error) {}
  } else if (compressed) {
    return `${process.env.PUBLIC_URL}/db/users/${id}/img/${id}comp.png`;
  } else {
    return `${process.env.PUBLIC_URL}/db/users/${id}/img/${id}.png`;
  }
};

/**
 * Normalizes string, removing accents etc..
 * @param str
 * @returns normalized string
 */
const normalizedString = (str: string) => {
  // biome-ignore lint/suspicious/noMisleadingCharacterClass: <explanation>
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

/** Puts commas and decimals to number
 * @param  {number} x
 * @return {string} xxx,xxx.xx €
 */
const numberWithCommas = (x: number) => {
  const n = Number(x)
    .toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${n} €`;
};
/** Transforms the date object into a dd/mm/yyyy string
 * @param  {string} d DateObject
 * @return {string} dd/mm/yyyy
 */
const dateToString = (d: Date | number, withHours = false) => {
  if (d instanceof Date) {
    const day = `0${d.getDate()}`.slice(-2);
    const month = `0${String(d.getMonth() + 1)}`.slice(-2);
    const year = d.getFullYear().toString();
    let dateString = `${day}/${month}/${year}`;
    if (withHours) {
      dateString += " ";
      dateString += `0${d.getHours()}`.slice(-2);
      dateString += "h";
      dateString += `0${d.getMinutes()}`.slice(-2);
    }
    return dateString;
  }
  const newD = new Date(d);
  const day = `0${newD.getDate()}`.slice(-2);
  const month = `0${String(newD.getMonth() + 1)}`.slice(-2);
  const year = newD.getFullYear().toString();
  let dateString = `${day}/${month}/${year}`;
  if (withHours) {
    dateString += " ";
    dateString += `0${newD.getHours()}`.slice(-2);
    dateString += "h";
    dateString += `0${newD.getMinutes()}`.slice(-2);
  }
  return dateString;
};

/** Transforms the date string into Date Object
 * @param  {string} date dd/mm/yyy or dd-mm-yyyy
 * @return {Date Object}
 */
const inputToDate = (date: string | number) => {
  if (!date) {
    return new Date();
  }
  if (typeof date === "string") {
    let splitter = "";
    if (date.includes("/")) {
      splitter = "/";
    } else if (date.includes("-")) {
      splitter = "-";
    } else {
      return new Date();
    }
    const arr: Array<string> = date.split(splitter);
    if (arr.length <= 1) {
      // when the state is initializing, the date doesn't exist yet
      return new Date();
    }
    const year = parseInt(arr[2]);
    const month = parseInt(arr[1]) - 1;
    const day = parseInt(arr[0]);
    return new Date(year, month, day);
  }
  if (typeof date === "number") {
    // then it is a timestamp
    return new Date(date);
  }
  return new Date();
};

/** Extends a date input to a string format
 * @param  {string | Date | number} date in the form of dd/mm/yyyy, Date or timestamp
 * @returns  {string} string in the format eg: Thu, May 14, 2021
 */
const extendDate = (date: string | Date | number) => {
  let newDate = date;
  if (typeof date === "string") {
    newDate = inputToDate(date);
  } else if (date instanceof Date) {
    newDate = date;
  } else {
    newDate = new Date(date);
  }
  return newDate.toLocaleDateString("en-us", {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/** Gets hours of timestamp date
 * @param  {number} date in the form of dd/mm/yyyy, Date or timestamp
 * @returns  {number} hours of timestamp
 */
const getHoursFromTimestamp = (date: number) => {
  const newDate = new Date(date);
  return newDate.getHours();
};

/** Gets minutes of timestamp date
 * @param  {number} date in the form of dd/mm/yyyy, Date or timestamp
 * @returns  {number} hours of timestamp
 */
const getMinutesFromTimestamp = (date: number) => {
  const newDate = new Date(date);
  return newDate.getMinutes();
};
/** Gets minutes of timestamp date in a string form "00"
 * @param  {number} date in the form of dd/mm/yyyy, Date or timestamp
 * @returns  {number} hours of timestamp
 */
const getMinutesInStringFromTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  const minutes = date.getMinutes();
  return `0${minutes}`.slice(-2);
};

/** Gets hours of timestamp date in a string form "00"
 * @param  {number} date in the form of dd/mm/yyyy, Date or timestamp
 * @returns  {number} hours of timestamp
 */
const getHoursInStringFromTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  const hours = date.getHours();
  return `0${hours}`.slice(-2);
};

/** Formats the text of the column header, inserting spaces and uppercases,
based on database key's
 * @param  {string} keyId database key Id
 * @returns  {string} string in the format eg: Thu, May 14, 2021
 */
const setColumnText = (keyId: string) => {
  // Transforms the keys to upper values
  for (let i = 0; i < keyId.length; i++) {
    if (keyId.charAt(i) === keyId.charAt(i).toUpperCase()) {
      // insert space between the string
      let newVal = `${keyId.substr(0, i)} ${keyId.substr(i)}`;
      newVal = newVal.charAt(0).toUpperCase() + newVal.slice(1);
      return newVal;
    }
  }
  return keyId.charAt(0).toUpperCase() + keyId.slice(1);
};

/** Sorting formatter for Dates
 * @param  {Date} date1
 * @param  {Date} date2
 * @returns  {number} difference
 */
function dateComparator(date1: string, date2: string) {
  const date1Number = dateStringComparableNumber(date1);
  const date2Number = dateStringComparableNumber(date2);

  if (date1Number === null && date2Number === null) {
    return 0;
  }
  if (date1Number === null) {
    return -1;
  }
  if (date2Number === null) {
    return 1;
  }

  return date1Number - date2Number;
}
/** Sorting formatter for Dates
 * @param  {Date} date1
 * @param  {Date} date2
 * @returns  {number} difference
 */
const dateWithHoursComparator = (date1: string, date2: string) => {
  const date1Number = dateWithHoursComparableNumber(date1);
  const date2Number = dateWithHoursComparableNumber(date2);

  if (date1Number === null && date2Number === null) {
    return 0;
  }
  if (date1Number === null) {
    return -1;
  }
  if (date2Number === null) {
    return 1;
  }

  return date1Number - date2Number;
};

/** eg 29/08/2004 gets converted to 20040829
 * @param  {string} date
 * @returns  {number}
 */
function dateStringComparableNumber(date: string) {
  if (date === undefined || date === null || date.length !== 10) {
    return null;
  }

  const yearNumber = parseInt(date.substring(6, 10));
  const monthNumber = parseInt(date.substring(3, 5));
  const dayNumber = parseInt(date.substring(0, 2));

  const newD = new Date(yearNumber, monthNumber - 1, dayNumber);
  return newD.getTime();
}

/** eg 29/08/2004 22h00 gets converted to 20040829 (example)
 * @param  {string} date
 * @returns  {number}
 */
function dateWithHoursComparableNumber(date: string) {
  if (date === undefined || date === null || date.length < 10) {
    return null;
  }
  const yearNumber = parseInt(date.substring(6, 10));
  const monthNumber = parseInt(date.substring(3, 5));
  const dayNumber = parseInt(date.substring(0, 2));
  const hours = parseInt(date.substring(11, 13));
  const minutes = parseInt(date.substring(14));

  const newD = new Date(yearNumber, monthNumber - 1, dayNumber, hours, minutes);

  return newD.getTime();
}

/** Calculates remaining hours between a given date, and now
 * @param  {Date} d
 * @param  {number} hours
 * @param  {number} minutes
 * @returns  {number}remaining hours
 */
const remainingHours = (date: string, hours: number | string, minutes: number | string) => {
  const now = new Date();
  const endDate = inputToDate(date);
  const offset = now.getTimezoneOffset();
  const diferentialoffsetmili = (offset + 60) * 60 * 1000;
  if (typeof hours === "string") hours = parseInt(hours);
  if (typeof minutes === "string") minutes = parseInt(minutes);
  endDate.setHours(hours);
  endDate.setMinutes(minutes);

  const endTime = endDate.getTime() / 1000; // ISO format
  const elapsed = (now.getTime() - diferentialoffsetmili) / 1000;
  const totalSec = endTime - elapsed;
  const h = Math.round(totalSec / 3600);
  // var m = (totalSec / 60) % 60;
  // var s = totalSec % 60;
  //   var result = h + " hours, " + m + " minutes " + s + " seconds";

  return h;
};
/** Checks if the date is in tha past week, returning the corresponding day
 * @param  {Date} d
 * @returns  {string}past week date, or the corresponding date
 */
const isDateInPastWeek = (d: Date) => {
  const last7Days = new Date();
  const todayDay = last7Days.getDate();
  last7Days.setDate(d.getDate() - 7);
  const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // if the date is in the on the current week, then return the day of the week
  if (d > last7Days) {
    const currDay = d.getDate();
    if (currDay === todayDay) {
      return "Today";
    }
    if (currDay === todayDay - 1) {
      return "Yesterday";
    }
    const currWeekDay = d.getDay();
    return weekdays[currWeekDay];
  }
  const day = `0${d.getDate()}`.slice(-2);
  const month = `0${String(d.getMonth() + 1)}`.slice(-2);
  const year = d.getFullYear().toString();
  const date = `${day}/${month}/${year}`;
  return date;
};

/** Object with multiple functions to calculate difference between dates, in
years, months, weeks and days
 * @param  {Date}d1 date 1
 * @param  {Date}d2 date 2
 * @returns {number} respctive difference
 */
const DateDiff = {
  inDays: (d1: Date, d2: Date) => {
    const t2 = d2.getTime();
    const t1 = d1.getTime();

    return parseInt(Number((t2 - t1) / (24 * 3600 * 1000)).toFixed(20));
  },

  inWeeks: (d1: Date, d2: Date) => {
    const t2 = d2.getTime();
    const t1 = d1.getTime();

    return parseInt(Number((t2 - t1) / (24 * 3600 * 1000 * 7)).toFixed(20));
  },

  inMonths: (d1: Date, d2: Date) => {
    const d1Y = d1.getFullYear();
    const d2Y = d2.getFullYear();
    const d1M = d1.getMonth();
    const d2M = d2.getMonth();

    return parseInt(Number(d2M + 12 * d2Y - (d1M + 12 * d1Y)).toFixed(20));
  },

  inYears: (d1: Date, d2: Date) =>
    parseInt(Number(d2.getFullYear() - d1.getFullYear()).toFixed(20)),
};

/** Returns a string indicanting the difference betewwn now and a previosu date
 * @param  {number} timestamp a date timestamp of the respective date to get
 * difference of
 * @returns {string} respective difference in a string form
 */
const dateDifference = (timestamp: number) => {
  const d1 = new Date();
  const d2 = new Date(timestamp);
  const inYears = Math.abs(DateDiff.inYears(d1, d2));
  const inMonths = Math.abs(DateDiff.inMonths(d1, d2));
  const inWeeks = Math.abs(DateDiff.inWeeks(d1, d2));
  const inDays = Math.abs(DateDiff.inDays(d1, d2));
  const hoursMinutes = `${`0${d2.getHours()}`.slice(-2)}:${`0${d2.getMinutes()}`.slice(-2)}`;

  if (inYears) {
    if (inYears > 1) {
      return `${inYears} years ago`;
    }
    if (inYears === 1) {
      return `${inYears} year ago`;
    }
  }
  if (inMonths && inDays > 30) {
    if (inMonths > 1) {
      return `${inMonths} months ago`;
    }
    if (inMonths === 1) {
      return `${inMonths} month ago`;
    }
  }
  if (inWeeks) {
    if (inWeeks > 1) {
      return `${inWeeks} weeks ago`;
    }
    if (inWeeks === 1) {
      return `${inWeeks} week ago`;
    }
  }
  if (inDays) {
    if (inDays > 1) {
      return `${inDays} days ago`;
    }
    if (inDays === 1) {
      return `Yesterday`;
    }
  } else {
    // today
    return hoursMinutes;
  }
};

/** Decodes a string to its original form
 * @param  {string} s encoded string
 */
const getDecodedString = (s: string) => {
  return decodeData(decodeData(decodeData(s)));
};

/** Encodes a string to for database and URL, URI compliant string
 * @param  {string} s encoded string
 */
const getEncodedString = (s: string) => {
  return encodeData(encodeData(s));
};

/** Encodes string to be URI complacement
 * @param  {string} s
 * @returns  {string} encoded string
 */
const encodeData = (s: string) => {
  return encodeURIComponent(s)
    .replace(/-/g, "%2D")
    .replace(/_/g, "%5F")
    .replace(/\./g, "%2E")
    .replace(/!/g, "%21")
    .replace(/~/g, "%7E")
    .replace(/\*/g, "%2A")
    .replace(/'/g, "%27")
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29");
};

/** Dncodes complacement URI string
 * @param  {string} s
 * @returns  {string} decoded string
 */
const decodeData = (s: string) => {
  try {
    return decodeURIComponent(
      s
        .replace(/%2D/g, "-")
        .replace(/%5F/g, "_")
        .replace(/%2E/g, ".")
        .replace(/%21/g, "!")
        .replace(/%7E/g, "~")
        .replace(/%2A/g, "*")
        .replace(/%27/g, "'")
        .replace(/%28/g, "(")
        .replace(/%29/g, ")"),
    );
  } catch (e) {
    // this happens when the string is tottally decoded, so we return the
    // original, for example. An original string containing "!" will throw an
    // error. And we now that "!" is the originally decoded string.

    return s;
  }
};

/** Sort users based on name
 * @param  {[string, PersonalInformation, boolean][]} sortableUsers array of
 * users to be sorted
 * @returns   {sortableUsers} sorted users
 */
const sortUsers = (sortableUsers: [string, PersonalInformation, boolean][]) => {
  if (sortableUsers) {
    sortableUsers.sort(
      (a: [string, PersonalInformation, boolean], b: [string, PersonalInformation, boolean]) => {
        if (a[1].name && b[1].name) {
          // Concatenate strings for sorting
          const nameA = a[1].name.replaceAll(" ", "");
          const nameB = b[1].name.replaceAll(" ", "");
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
          return 0;
        }
        return 0;
      },
    );
  }
  return sortableUsers;
};
/** Puts the users list in the select object manner
 * @param  {[string, PersonalInformation, boolean][]} sortableUsers array of
 * users to be sorted
 * @returns   extended string eg: "inTeam" -> "In Team"
 */
const buildUserSelectOptions = (sortableUsers: [string, PersonalInformation, boolean][]) => {
  const selectUserOptions: selectOption[] = [];
  sortableUsers.forEach((user) => {
    const userKey = user[0];
    const userInfo = user[1];
    const userLabel = `${userInfo.name}, ${userInfo.department}`;
    const userOption = {
      value: userKey,
      label: userLabel,
    };
    selectUserOptions.push(userOption);
  });
  return selectUserOptions;
};

/** Gets users to build the select assignment
 * @param  {Function} setUserOptions update user options state
 * @param  {Function} setUsersMetadata update users metadata state
 */
const getUserAssignmentOptions = (setUserOptions: Function, setUsersMetadata: Function) => {
  get(ref(db, "private/usersMetadata")).then((snapshot) => {
    const allUsers = snapshot.val();
    setUsersMetadata(allUsers);

    const sortableUsers: [string, PersonalInformation, boolean][] = [];
    snapshot.forEach((user) => {
      if (user.key) {
        sortableUsers.push([user.key, user.val().pinfo, user.val().pinfo.inTeam]);
      }
    });
    const sortedUsers = sortUsers(sortableUsers);

    const options = buildUserSelectOptions(sortedUsers);
    setUserOptions(options);
  });
};

/** Sets users to build the select assignment
 * @param  {Function} setUserOptions update user options state
 * @param  {Function} setUsersMetadata update users metadata state
 */
const setUserAssignmentOptions = (
  setUserOptions: Function,
  usersMetadata: UserMetadata,
  department?: Department,
) => {
  const sortableUsersStart: [string, PersonalInformation, boolean][] = [];
  const sortableUsersEnd: [string, PersonalInformation, boolean][] = [];
  Object.entries(usersMetadata).forEach(([userId, user]) => {
    const inTeam = !!user.pinfo.inTeam;
    if (userId) {
      if (department) {
        if (user.pinfo.department === department.description) {
          sortableUsersStart.push([userId, user.pinfo, inTeam]);
        } else {
          sortableUsersEnd.push([userId, user.pinfo, inTeam]);
        }
      } else {
        sortableUsersStart.push([userId, user.pinfo, inTeam]);
      }
    }
    const sortedUsersStart = sortUsers(sortableUsersStart);
    const sortedUsersEnd = sortUsers(sortableUsersEnd);

    const sortedUsers = [...sortedUsersStart, ...sortedUsersEnd];

    const options = buildUserSelectOptions(sortedUsers);
    setUserOptions(options);
  });
};

/**
 * Retrieves user profile URL
 * @param userId
 * @returns user url
 */
const getUserProfileLink = (userId: string) => {
  return `/profile/u/${userId}`;
};

/**
 * Retrieve user image URL stored in server
 * @param userId
 * @param compressed
 * @returns
 */
const getUserImg = (userId: string, compressed = false) => {
  if (!compressed) return `${process.env.PUBLIC_URL}/db/users/${userId}/img/${userId}.png`;
  return `${process.env.PUBLIC_URL}/db/users/${userId}/img/${userId}_comp.png`;
};

/**
 * Sends a notification to the specified user
 * @param sendTo user id
 * @param sentBy user id
 * @param title title of the notification
 * @param description description of the notification
 * @param urlPath url path
 * @param urlObject optional url object to open modals etc, like in tasks and budget
 * @param notifType type of the notification, to choose icon basically
 * @param color color of the notification
 */
const sendNotification = (
  sendTo: string,
  sentBy: string,
  title: string,
  description: string,
  urlPath: string,
  urlObject: UrlObject | null,
  notifType: string,
  color: string,
) => {
  if (sendTo !== sentBy) {
    const notification: Notification = {
      sentBy: sentBy,
      title: title,
      description: description,
      urlPath: urlPath,
      urlObject: urlObject ? urlObject : null,
      type: notifType,
      color: color,
      timestamp: new Date().getTime(),
    };
    push(ref(db, `private/usersNotifications/${sendTo}/all`), notification);
    push(ref(db, `private/usersNotifications/${sendTo}/new`), notification);
  }
};

/**
 * Transforms "thisIsAString" to "This Is A String"
 * @param str
 * @returns
 */
const pascalStringToTitleCase = (str: string) => {
  const result = str.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

/**
 * Checks if the object has childs or not
 * @param obj
 * @returns
 */
const objectExists = (obj: {}) => {
  return obj && Object.keys(obj).length !== 0 && obj.constructor === Object;
};

/**
 * Checks if user has permissions
 * @param user
 * @returns
 */
const userHasPermission = (user: userContext | null, matchUserId?: string) => {
  if (!user) return false;
  if (matchUserId && user.id === matchUserId) return true;

  const position = getLatestPosition(user.position);

  if (
    position === "Team Leader" ||
    position === "Technical Director" ||
    position === "Head of Department" ||
    position === "Chief Engineer" ||
    position === "Project Manager" ||
    position === "Software Developer"
  ) {
    return true;
  }
  return false;
};

/**
 * Checks if user has admin permissions
 * @param user
 * @returns
 */
const userHasAdminPermissions = (user: userContext | null) => {
  if (!user) return false;

  const position = getLatestPosition(user.position);

  return (
    position === "Team Leader" ||
    position === "Head of Department" ||
    position === "Technical Director" ||
    position === "Chief Engineer" ||
    position === "Project Manager" ||
    position === "Software Developer"
  );
};

const getLatestPosition = (position: string | Record<string, string> | undefined): string => {
  if (!position) return "Unknown";
  if (typeof position === "string") return position;

  const years = Object.keys(position)
    .map(Number)
    .sort((a, b) => b - a);

  const latestYear = years[0]?.toString();
  return latestYear && position[latestYear] ? position[latestYear] : "Unknown";
};

/**
 * Defines wheter a user can view a feature or not
 * @param featureName
 * @param applicationFeatures
 * @param isAdminUser
 * @param isGod
 * @returns
 */
const isFeatureVisible = (
  featureName: string,
  applicationFeatures: ApplicationFeatures,
  isAdminUser: boolean,
  isGod: boolean,
) => {
  if (Object.hasOwn(applicationFeatures, featureName)) {
    if (applicationFeatures[featureName].public) return true;
    if ((applicationFeatures[featureName].admin && isAdminUser) || isGod) return true;
  }
  return false;
};

export {
  buildUserSelectOptions,
  dateComparator,
  dateDifference,
  dateToString,
  dateWithHoursComparator,
  decodeData,
  encodeData,
  extendDate,
  getAllUsersMetadata,
  getAndSetAllUsersMetadata,
  getDecodedString,
  getEncodedString,
  getHoursFromTimestamp,
  getHoursInStringFromTimestamp,
  getLatestPosition,
  getMinutesFromTimestamp,
  getMinutesInStringFromTimestamp,
  getUserAssignmentOptions,
  getUserImg,
  getUserImgUrl,
  getUserProfileLink,
  inputToDate,
  isDateInPastWeek,
  isFeatureVisible,
  normalizedString,
  numberWithCommas,
  objectExists,
  pascalStringToTitleCase,
  remainingHours,
  sendNotification,
  setColumnText,
  setUserAssignmentOptions,
  sortUsers,
  swalMessage,
  toastrMessage,
  userHasAdminPermissions,
  userHasPermission,
};
